import { useEffect, useState } from 'react';
import { Climber } from './Climber';
import { resolveAssetUrl } from '@/shop/assetUrl';
import { loadStarterSkin } from '@/characters/starterSkin';

/** Idle art of the default (starter) character, loaded once and cached. Used as
 * the avatar fallback when the player has neither a photo nor an equipped
 * character — shows the real starter skin (transparent art) instead of the bare
 * red_*.png procedural climber (opaque bg → solid box). */
function useStarterIdleUrl(enabled: boolean): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled || url) return;
    let alive = true;
    void loadStarterSkin().then((s) => {
      if (alive && s?.idleUrl) setUrl(s.idleUrl);
    });
    return () => {
      alive = false;
    };
  }, [enabled, url]);
  return url;
}

/** Frame styling — derived from the equipped avatarFrame cosmetic's `data`
 * payload. `imageUrl` (a transparent PNG ring overlay) wins; otherwise the
 * CSS `border` + `glow` ring is used. All fields optional. */
export interface AvatarFrameSpec {
  border?: string | null;
  glow?: string | null;
  imageUrl?: string | null;
}

interface AvatarProps {
  color?: 'red' | 'blue';
  size?: number;
  ring?: boolean;
  /** Player photo URL (server-issued). When set, renders the photo cropped
   *  to a circle instead of the procedural climber. */
  photoUrl?: string | null;
  /** Equipped Character idle PNG. Used as a fallback avatar when the player
   *  has NOT uploaded a photo — shows their current climber skin instead of
   *  the procedural default. Ignored when `photoUrl` is set. */
  characterUrl?: string | null;
  /** Equipped avatar frame — renders a colored ring + outer glow over the
   *  default red/blue rim. Pass null/undefined to skip. */
  frame?: AvatarFrameSpec | null;
}

export function Avatar({ color = 'red', size = 56, ring = true, photoUrl, characterUrl, frame }: AvatarProps) {
  const bg1 = color === 'red' ? '#3a1a3a' : '#1a2a4a';
  const bg2 = color === 'red' ? '#1E1B4B' : '#0a1230';
  const rim = color === 'red' ? 'var(--c-red)' : 'var(--c-blue)';
  // Uploaded photo wins; otherwise the equipped character art; otherwise the
  // default starter skin (so a fresh player shows the real default character,
  // not the opaque red_*.png procedural climber).
  const photo = resolveAssetUrl(photoUrl);
  const equipped = !photo ? resolveAssetUrl(characterUrl) : null;
  const starterIdle = useStarterIdleUrl(!photo && !equipped);
  const character = equipped ?? resolveAssetUrl(starterIdle);
  const resolved = photo ?? character;

  // Frame: an image overlay wins; otherwise a CSS ring (border + glow).
  // Border thickness is proportional to size so it reads at any scale.
  const frameImage = resolveAssetUrl(frame?.imageUrl);
  const frameBorder = !frameImage ? frame?.border ?? null : null;
  const frameGlow = !frameImage ? frame?.glow ?? null : null;
  const borderWidth = Math.max(2, Math.round(size * 0.06));

  const inner = (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `radial-gradient(circle at 50% 30%, ${bg1}, ${bg2})`,
        border: ring && !frameBorder && !frameImage ? `2.5px solid ${rim}` : 'none',
        boxShadow: ring && !frameGlow && !frameImage
          ? `0 0 12px ${color === 'red' ? 'rgba(230,57,70,0.5)' : 'rgba(59,130,246,0.5)'}`
          : 'none',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {resolved ? (
        <img
          src={resolved}
          alt=""
          width={size}
          height={size}
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Full-body character art: bias the crop toward the top so the
            // head/upper body shows instead of the torso. Uploaded square
            // photos stay centered.
            objectPosition: character ? 'center top' : 'center',
            pointerEvents: 'none',
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: -6,
            transform: 'translateX(-50%)',
          }}
        >
          <Climber color={color} pose="idle" scale={(size / 52) * 0.95} />
        </div>
      )}
    </div>
  );

  // Image frame: overlay a transparent PNG ring on top of the avatar. The
  // overlay extends slightly beyond the avatar so decorative rings sit outside.
  if (frameImage) {
    const overlayPad = Math.round(size * 0.2);
    const outerSize = size + overlayPad * 2;
    return (
      <div
        style={{
          position: 'relative',
          width: outerSize,
          height: outerSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {inner}
        <img
          src={frameImage}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
      </div>
    );
  }

  if (!frameBorder && !frameGlow) return inner;

  // Outer frame wrapper — render the colored ring + glow around the avatar.
  const outerSize = size + borderWidth * 2;
  return (
    <div
      style={{
        width: outerSize,
        height: outerSize,
        borderRadius: '50%',
        padding: borderWidth,
        boxSizing: 'border-box',
        background: frameBorder ?? 'transparent',
        boxShadow: frameGlow ? `0 0 14px ${frameGlow}, 0 0 4px ${frameGlow}` : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {inner}
    </div>
  );
}
